/**
 * Population search infrastructure
 * Connects world data to population evaluation
 */

import type {
  PopulationData,
  WorldsData,
  ZoneTemplatesData,
  CellData,
  ZoneData,
  ZoneTemplateNode,
} from '@/qud/types.js';
import type { StratumRange } from './types.js';
import { PopulationEvaluator, calculateZoneTier } from './PopulationEvaluator.js';
import type { RpmBundle, CompressedObject, CompressedRpmData } from '@/qud/dataLoader.js';

/**
 * Population reference with probability multiplier
 */
interface PopulationRef {
  table: string;
  probability: number; // Compound probability from all parent nodes
}

/**
 * TileBuilding spawn reference with probability
 */
interface TileBuildingSpawn {
  blueprint: string;
  probability: number; // ChancePerVault / 100
}

/**
 * Extract TileBuilding spawns from zone builders.
 *
 * TileBuilding uses CustomCharMap to map characters in vault patterns to blueprints.
 * Example: "a@Alchemist,-@Alchemist Table" maps 'a' -> "Alchemist", '-' -> "Alchemist Table"
 *
 * For simplicity, we treat any blueprint in CustomCharMap as potentially spawning,
 * with probability = ChancePerVault / 100 (or 1 if ChancePerVault >= 100).
 *
 * NOTE: This is an approximation. The actual spawn depends on:
 * - Which vault pattern is selected (from VaultStartMarker to VaultEndMarker)
 * - Whether the specific character appears in that vault pattern
 * - The ChancePerVault probability
 *
 * For now we assume if a character is in CustomCharMap, it will spawn.
 */
function getTileBuildingSpawns(zone: ZoneData): TileBuildingSpawn[] {
  const spawns: TileBuildingSpawn[] = [];

  const allBuilders = [...zone.builders, ...zone.postbuilders];

  for (const builder of allBuilders) {
    if (builder.class === 'TileBuilding' && builder.CustomCharMap) {
      const customCharMap = builder.CustomCharMap as string;
      // ChancePerVault defaults to 100 if NumberOfVaults > 0
      const chancePerVault = (builder.ChancePerVault as number) ?? 100;
      const numberOfVaults = (builder.NumberOfVaults as number) ?? 0;

      // Only process if there are vaults with a chance to spawn
      if (numberOfVaults > 0 && chancePerVault > 0) {
        const probability = Math.min(chancePerVault / 100, 1);

        // Parse CustomCharMap: "a@Alchemist,-@Alchemist Table,..."
        const entries = customCharMap.split(',');
        for (const entry of entries) {
          if (entry && entry.includes('@')) {
            const [, blueprint] = entry.split('@');
            if (blueprint && blueprint !== '(clear)') {
              spawns.push({ blueprint, probability });
            }
          }
        }
      }

      // Also check for second vault set (VaultStartMarker2, etc.)
      const chancePerVault2 = (builder.ChancePerVault2 as number) ?? 0;
      const numberOfVaults2 = (builder.NumberOfVaults2 as number) ?? 0;

      if (numberOfVaults2 > 0 && chancePerVault2 > 0) {
        // Same CustomCharMap is used for both vault sets
        // This could add duplicates, but that's OK since we're tracking expected spawns
        const probability2 = Math.min(chancePerVault2 / 100, 1);

        const entries = customCharMap.split(',');
        for (const entry of entries) {
          if (entry && entry.includes('@')) {
            const [, blueprint] = entry.split('@');
            if (blueprint && blueprint !== '(clear)') {
              spawns.push({ blueprint, probability: probability2 });
            }
          }
        }
      }
    }
  }

  return spawns;
}

/**
 * Extract population table references from zone builders with probability weights.
 *
 * LIMITATIONS:
 * - Criteria like "Isolated", "Furthest", "Connection", "RegionHasSemanticTag:X"
 *   require knowing the zone layout which we cannot determine statically.
 *   These are treated as having ~unknown probability and currently contribute
 *   to the expected spawn count (may overestimate).
 * - Exit nodes (<exit/>) that short-circuit processing are not modeled.
 *   This could overestimate spawns from later populations in the sequence.
 */
function getPopulationsFromZone(zone: ZoneData, zoneTemplates: ZoneTemplatesData): PopulationRef[] {
  const populations: PopulationRef[] = [];

  const allBuilders = [...zone.builders, ...zone.postbuilders];

  for (const builder of allBuilders) {
    // ZoneTemplate:X builder
    if (builder.class.startsWith('ZoneTemplate:')) {
      const templateName = builder.class.substring('ZoneTemplate:'.length);
      const template = zoneTemplates.templates[templateName];
      if (template) {
        // Collect population tables with their probability weights
        const collectFromNodes = (nodes: ZoneTemplateNode[], parentProb: number) => {
          for (const node of nodes) {
            // Apply this node's chance to the probability chain
            const nodeChance = node.chance !== undefined ? node.chance / 100 : 1;
            const effectiveProb = parentProb * nodeChance;

            // Criteria handling: we cannot statically determine if criteria will be met.
            // For now, we include these populations but this may overestimate.
            // TODO: Some criteria could be modeled with heuristics:
            // - "Isolated": ~5-15% of regions are isolated in caves
            // - "Connection": ~10-20% of regions have zone connections
            // - "Furthest": exactly 1 region per zone, so ~1/regionCount
            // For now, we assume criteria always pass (probability = 1)

            if (node.type === 'population') {
              populations.push({ table: node.table, probability: effectiveProb });
            } else if (node.type === 'group') {
              collectFromNodes(node.children, effectiveProb);
            }
          }
        };

        // Global populations apply once per zone
        collectFromNodes(template.global, 1);

        // eachRegion populations apply to each region
        // A typical zone has ~10-20 regions based on regionSize
        // For now we use 1 as a multiplier (per-region expected value)
        collectFromNodes(template.eachRegion, 1);

        // singleRegion populations apply to one region
        collectFromNodes(template.singleRegion, 1);
      }
    }

    // FactionEncounters builder - has Population attribute (skipped in v0)
    // Direct Population builder - has Table attribute
    if (builder.class === 'Population' && builder.Table) {
      populations.push({ table: builder.Table as string, probability: 1 });
    }
  }

  return populations;
}

/**
 * Resolve cell inheritance and build a full 3D zone map for the parasang.
 *
 * The game uses a 3D array LevelBlueprint[x, y, level] where each position
 * holds exactly ONE ZoneBlueprint. Zone definitions are processed in XML order,
 * and later zones OVERWRITE earlier ones for the same position.
 *
 * A parasang is a 3x3 grid of zones, so we need to track all 9 positions.
 * Returns a Map from "x,y,level" -> ZoneData.
 */
function buildParasangZoneMap(
  cell: CellData,
  cellsByName: Map<string, CellData>,
  maxLevel: number = 50
): Map<string, ZoneData> {
  // Key format: "x,y,level" where x,y are 0-2
  const zoneMap = new Map<string, ZoneData>();

  // If this cell inherits, first populate from parent
  if (cell.inherits) {
    const parentCell = cellsByName.get(cell.inherits);
    if (parentCell) {
      const parentMap = buildParasangZoneMap(parentCell, cellsByName, maxLevel);
      for (const [key, zone] of parentMap) {
        zoneMap.set(key, zone);
      }
    }
  }

  // Now apply this cell's zones - they overwrite parent zones for the same positions
  // Zones are processed in XML/array order, later ones overwrite earlier ones
  for (const zone of cell.zones) {
    const xMin = zone.xMin ?? 0;
    const xMax = zone.xMax ?? 2;
    const yMin = zone.yMin ?? 0;
    const yMax = zone.yMax ?? 2;

    // Apply zone to all positions and levels it covers
    for (let x = xMin; x <= xMax; x++) {
      for (let y = yMin; y <= yMax; y++) {
        for (let level = zone.levelMin; level <= Math.min(zone.levelMax, maxLevel); level++) {
          const key = `${x},${y},${level}`;
          zoneMap.set(key, zone);
        }
      }
    }
  }

  return zoneMap;
}

/**
 * Population search class
 */
export class PopulationSearch {
  private worlds: WorldsData;
  private zoneTemplates: ZoneTemplatesData;
  private evaluator: PopulationEvaluator;
  private rpmBundle: RpmBundle | null = null;

  constructor(
    populations: PopulationData,
    worlds: WorldsData,
    zoneTemplates: ZoneTemplatesData,
    rpmBundle?: RpmBundle
  ) {
    this.worlds = worlds;
    this.zoneTemplates = zoneTemplates;
    this.evaluator = new PopulationEvaluator(populations);
    this.rpmBundle = rpmBundle ?? null;
  }

  // Cached cell lookup for performance
  private cellsByNameCache: Map<string, Map<string, CellData>> = new Map();
  // Cache parasang zone maps: "worldName:terrainType" -> ("x,y,level" -> ZoneData)
  private parasangZoneCache: Map<string, Map<string, ZoneData>> = new Map();
  // Cache zone -> populations mapping (keyed by zone object reference)
  private zonePopulationsCache: Map<ZoneData, PopulationRef[]> = new Map();
  // Cache zone -> TileBuilding spawns (keyed by zone object reference)
  private zoneTileBuildingCache: Map<ZoneData, TileBuildingSpawn[]> = new Map();
  // Cache zone -> static spawn count for a blueprint
  private zoneStaticSpawnCache: Map<string, number> = new Map();

  /**
   * Count static spawns of a blueprint in a zone's RPM maps
   */
  private countStaticSpawnsInZone(zone: ZoneData, blueprintName: string): number {
    if (!this.rpmBundle || !zone.maps || zone.maps.length === 0) {
      return 0;
    }

    let count = 0;

    for (const mapName of zone.maps) {
      // Check cache
      const cacheKey = `${mapName}:${blueprintName}`;
      const cached = this.zoneStaticSpawnCache.get(cacheKey);
      if (cached !== undefined) {
        count += cached;
        continue;
      }

      // Look up the RPM directly
      const rpm: CompressedRpmData | undefined = this.rpmBundle.rpms[mapName];
      if (!rpm) {
        this.zoneStaticSpawnCache.set(cacheKey, 0);
        continue;
      }

      // Count occurrences of the blueprint in this RPM
      let mapCount = 0;
      const cells: CompressedObject[][] = Object.values(rpm.cells);
      for (const cell of cells) {
        for (const obj of cell) {
          const idx = typeof obj === 'number' ? obj : obj[0];
          if (this.rpmBundle.strings[idx] === blueprintName) {
            mapCount++;
          }
        }
      }

      this.zoneStaticSpawnCache.set(cacheKey, mapCount);
      count += mapCount;
    }

    return count;
  }

  /**
   * Calculate expected spawns for a blueprint at a specific terrain and stratum
   * Returns expected spawns across all 9 zones in the parasang (3x3 grid)
   *
   * NOTE: Uses correct zone resolution matching the game's behavior.
   * The game has exactly ONE ZoneBlueprint per (x, y, level) position.
   * When multiple zone definitions overlap, later definitions overwrite earlier ones.
   * A parasang contains 9 zones (3x3), each potentially with different zone definitions.
   */
  calculateExpectedSpawnsForTerrain(
    blueprintName: string,
    terrainType: string,
    terrainRegionTier: number,
    stratumRange: StratumRange,
    worldName: string = 'JoppaWorld'
  ): number {
    const world = this.worlds.worlds[worldName];
    if (!world) return 0;

    // Build cell lookup (cached)
    let cellsByName = this.cellsByNameCache.get(worldName);
    if (!cellsByName) {
      cellsByName = new Map<string, CellData>();
      for (const cell of world.cells) {
        cellsByName.set(cell.name, cell);
      }
      this.cellsByNameCache.set(worldName, cellsByName);
    }

    // Get parasang zone map for this terrain (cached)
    const cacheKey = `${worldName}:${terrainType}`;
    let parasangZoneMap = this.parasangZoneCache.get(cacheKey);
    if (!parasangZoneMap) {
      // Find cell for terrain (exact match or *Default)
      let matchedCell: CellData | undefined;
      for (const cell of world.cells) {
        if (cell.applyTo === terrainType) {
          matchedCell = cell;
          break;
        }
      }
      if (!matchedCell) {
        matchedCell = world.cells.find((c) => c.applyTo === '*Default');
      }
      if (!matchedCell) return 0;

      // Build parasang zone map (handles inheritance and overwriting)
      parasangZoneMap = buildParasangZoneMap(matchedCell, cellsByName, stratumRange.max);
      this.parasangZoneCache.set(cacheKey, parasangZoneMap);
    }

    let totalExpected = 0;

    // For each of the 9 zones in the parasang (3x3 grid)
    for (let zoneX = 0; zoneX <= 2; zoneX++) {
      for (let zoneY = 0; zoneY <= 2; zoneY++) {
        // For each level in the stratum range
        for (let level = stratumRange.min; level <= stratumRange.max; level++) {
          const zoneKey = `${zoneX},${zoneY},${level}`;
          const zone = parasangZoneMap.get(zoneKey);
          if (!zone) continue;

          // Get populations for this zone (cached)
          let zonePopulations = this.zonePopulationsCache.get(zone);
          if (!zonePopulations) {
            zonePopulations = getPopulationsFromZone(zone, this.zoneTemplates);
            this.zonePopulationsCache.set(zone, zonePopulations);
          }

          const zonetier = calculateZoneTier(level, terrainRegionTier);
          const context = { zonetier };

          for (const popRef of zonePopulations) {
            const expected = this.evaluator.findBlueprintExpectedSpawns(
              popRef.table,
              blueprintName,
              context
            );
            // Apply the probability from zone template criteria/chance
            totalExpected += expected * popRef.probability;
          }

          // Add static spawns from zone's RPM maps
          const staticSpawns = this.countStaticSpawnsInZone(zone, blueprintName);
          totalExpected += staticSpawns;

          // Add TileBuilding spawns (from CustomCharMap)
          let tileBuildingSpawns = this.zoneTileBuildingCache.get(zone);
          if (!tileBuildingSpawns) {
            tileBuildingSpawns = getTileBuildingSpawns(zone);
            this.zoneTileBuildingCache.set(zone, tileBuildingSpawns);
          }

          for (const spawn of tileBuildingSpawns) {
            if (spawn.blueprint === blueprintName) {
              totalExpected += spawn.probability;
            }
          }
        }
      }
    }

    return totalExpected;
  }

  /**
   * Calculate expected spawns for all map tiles
   * Returns map of tileKey -> expected spawns
   */
  calculateMapExpectedSpawns(
    blueprintName: string,
    tiles: Array<{ x: number; y: number; terrainType: string; regionTier?: number }>,
    stratumRange: StratumRange,
    worldName: string = 'JoppaWorld'
  ): Map<string, number> {
    const results = new Map<string, number>();

    // Group tiles by terrain type + region tier to avoid redundant computation
    // This is a major optimization: instead of evaluating 2000+ tiles individually,
    // we only evaluate ~100-200 unique terrain+tier combinations
    const terrainGroups = new Map<string, boolean>();
    for (const tile of tiles) {
      const regionTier = tile.regionTier ?? 0;
      const groupKey = `${tile.terrainType}:${regionTier}`;
      terrainGroups.set(groupKey, true);
    }

    // Calculate expected spawns per unique terrain type
    const terrainExpected = new Map<string, number>();
    for (const groupKey of terrainGroups.keys()) {
      const [terrainType, regionTierStr] = groupKey.split(':');
      const regionTier = parseInt(regionTierStr, 10);

      const expected = this.calculateExpectedSpawnsForTerrain(
        blueprintName,
        terrainType,
        regionTier,
        stratumRange,
        worldName
      );

      if (expected > 0) {
        terrainExpected.set(groupKey, expected);
      }
    }

    // Apply to all tiles
    for (const tile of tiles) {
      const regionTier = tile.regionTier ?? 0;
      const groupKey = `${tile.terrainType}:${regionTier}`;
      const expected = terrainExpected.get(groupKey);
      if (expected !== undefined && expected > 0) {
        const key = `${tile.x},${tile.y}`;
        results.set(key, expected);
      }
    }

    return results;
  }

  /**
   * Get all unique population table names in a world
   */
  getAllPopulationsInWorld(worldName: string = 'JoppaWorld'): Set<string> {
    const result = new Set<string>();
    const world = this.worlds.worlds[worldName];
    if (!world) return result;

    for (const cell of world.cells) {
      for (const zone of cell.zones) {
        const zonePops = getPopulationsFromZone(zone, this.zoneTemplates);
        for (const popRef of zonePops) {
          result.add(popRef.table);
        }
      }
    }

    return result;
  }
}
